import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { bdDashboardAPI } from "../../api";
import DashboardCard from "../lightning/dashboard/DashboardCard";
import WplActiveWindfarmsSection from "./WplActiveWindfarmsSection";
import WplGlobalViewSection from "./WplGlobalViewSection";
import { chartFont, downloadCsvFile, formatValue } from "./wplDashboardHelpers";
import "./wpl-dashboard.css";

const CURRENT_YEAR = new Date().getFullYear();

// Screensaver mode: auto-cycle the tabs on the office screen. Each step's
// dwell time starts once that step's data has finished loading.
const SCREENSAVER_STEPS = [
  { key: "global", tab: "global", dwellMs: 30000 },
  { key: "stats", tab: "stats", dwellMs: 30000 },
  { key: "lassie-overview", tab: "windfarms", phase: "overview", dwellMs: 30000 },
  { key: "lassie-farm", tab: "windfarms", phase: "farm", dwellMs: 30000 },
];
// If a step never reports its data as loaded, move on anyway.
const SCREENSAVER_FALLBACK_MS = 60000;

const RING_RADIUS = 8;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

// Draining ring + seconds until the screensaver moves to the next page.
// While a step's data is still loading, only the faint track is shown.
function ScreensaverCountdown({ dwell }) {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!dwell) return undefined;
    const intervalId = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(intervalId);
  }, [dwell]);

  const remainingSeconds = dwell
    ? Math.max(0, Math.ceil((dwell.start + dwell.ms - now) / 1000))
    : null;

  return (
    <span className="bd-ss-countdown">
      <svg className="bd-ss-ring" width="20" height="20" viewBox="0 0 20 20">
        <circle className="bd-ss-ring-track" cx="10" cy="10" r={RING_RADIUS} />
        {dwell && (
          <circle
            key={dwell.key}
            className="bd-ss-ring-progress"
            cx="10"
            cy="10"
            r={RING_RADIUS}
            strokeDasharray={RING_CIRCUMFERENCE}
            style={{ animationDuration: `${dwell.ms}ms` }}
          />
        )}
      </svg>
      <span className="bd-ss-seconds">
        {remainingSeconds !== null ? `${remainingSeconds}s` : "…"}
      </span>
    </span>
  );
}

const formatDateLabel = (rawDate) => {
  if (!rawDate) return "Unknown";
  const parsed = new Date(rawDate);
  if (Number.isNaN(parsed.getTime())) {
    return String(rawDate);
  }
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
};

const normalizeSeriesValue = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const theme = {
  background: "#123029",
  card: "#1b443e",
  border: "#3f7468",
  text: "#ffffff",
  muted: "#d3e3dd",
  accentAlert: "#f5894a",
  accentBrand: "#38b97c",
  grid: "#3f7468",
  tooltipBg: "#1b443e",
};

const chartSizes = {
  tickFontSize: 13,
  labelFontSize: 13,
  angledLabelHeight: 90,
  angledLabelRotation: -20,
};

const formatLongLabel = (label) => {
  if (!label) return "";
  const value = String(label);
  if (value.length <= 24) return value;
  return `${value.slice(0, 21)}...`;
};

const getValueTypeLabel = (entry) => {
  const rawValue = entry?.value_type;
  if (rawValue === null || rawValue === undefined) return null;
  if (typeof rawValue === "string") {
    const trimmed = rawValue.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (typeof rawValue === "number" || typeof rawValue === "boolean") {
    return String(rawValue);
  }
  return null;
};

const buildYAxisLabel = (labelValue) =>
  labelValue
    ? {
        value: labelValue,
        angle: -90,
        position: "insideLeft",
        dx: -10,
        fill: theme.muted,
        fontSize: chartFont(chartSizes.labelFontSize),
      }
    : undefined;

const mapValueStats = (stats) =>
  stats
    .filter((entry) => entry.type === "value")
    .map((entry) => ({
      title: entry.title,
      value: formatValue(entry.data),
      tooltip: entry.description,
    }));

const getEntryType = (entry) => String(entry?.type || "").toLowerCase();

const isBarChartType = (entryType) =>
  ["barchart", "bar_chart", "bar-chart", "bar"].includes(entryType);

const isGroupedBarType = (entryType) =>
  ["grouped_bar", "grouped-bar", "groupedbar"].includes(entryType);

const isTableType = (entryType) => entryType === "table";

const mapTimeSeriesData = (entry) => {
  if (!entry || !Array.isArray(entry.data)) return [];
  return entry.data
    .map((point) => {
      const rawDate = point.x ?? point.date ?? point.month ?? point[0];
      const rawValue = point.y ?? point.value ?? point.data ?? point[1];
      return {
        dateLabel: formatDateLabel(rawDate),
        value: normalizeSeriesValue(rawValue),
      };
    })
    .filter((point) => point.dateLabel);
};

const mapBarChartData = (entry) => {
  if (!entry) return [];
  if (Array.isArray(entry.data)) {
    return entry.data
      .map((point) => {
        const rawLabel = point.x ?? point.label ?? point[0];
        const rawValue = point.y ?? point.value ?? point[1];
        return {
          label: rawLabel ? String(rawLabel) : "Unknown",
          value: normalizeSeriesValue(rawValue) ?? 0,
        };
      })
      .filter((point) => point.label);
  }
  if (entry.data && typeof entry.data === "object") {
    return Object.entries(entry.data).map(([label, value]) => ({
      label,
      value: normalizeSeriesValue(value) ?? 0,
    }));
  }
  return [];
};

const mapGroupedBarData = (entry) => {
  if (!entry || !Array.isArray(entry.data)) return [];
  return entry.data
    .map((point) => ({
      label: point.x ? String(point.x) : "Unknown",
      internal: normalizeSeriesValue(point.internal) ?? 0,
      external: normalizeSeriesValue(point.external) ?? 0,
    }))
    .filter((point) => point.label);
};

const mapTableData = (entry) => {
  if (!entry || !entry.data || typeof entry.data !== "object") {
    return { columns: [], rows: [] };
  }
  const rawColumns = Array.isArray(entry.data.columns)
    ? entry.data.columns
    : [];
  const columns = rawColumns.map((column) => String(column));
  const rawRows = Array.isArray(entry.data.rows) ? entry.data.rows : [];
  const rows = rawRows.map((row) => {
    const rawMonth = row?.month ?? row?.date ?? row?.x;
    const monthLabel = formatDateLabel(rawMonth);
    const counts = row?.counts && typeof row.counts === "object" ? row.counts : {};
    const values = columns.reduce((acc, column) => {
      const normalized = normalizeSeriesValue(counts[column]);
      acc[column] = normalized ?? 0;
      return acc;
    }, {});
    return { monthLabel, values };
  });
  return { columns, rows };
};

const sanitizeFilenamePart = (value) => {
  if (!value) return "chart";
  const normalized = String(value)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return normalized || "chart";
};

const renderStatEntry = (entry, index, { timeseriesValueLabel = "Value" } = {}) => {
  const entryType = getEntryType(entry);
  const title = entry.title || "Chart";
  const description = entry.description || "";
  const valueTypeLabel = getValueTypeLabel(entry);
  const filenameBase = sanitizeFilenamePart(title);

  if (entryType === "timeseries") {
    const data = mapTimeSeriesData(entry);
    const csvRows = [
      ["date", "value"],
      ...data.map((point) => [point.dateLabel, point.value ?? ""]),
    ];
    return (
      <div key={`${title}-${index}`} className="bd-chart-card">
        <div className="bd-chart-header">
          <div className="bd-chart-header-row">
            <h3>{title}</h3>
            {data.length > 0 && (
              <button
                className="bd-download-csv-button"
                type="button"
                onClick={() =>
                  downloadCsvFile(`${filenameBase}-timeseries.csv`, csvRows)
                }
              >
                Download CSV
              </button>
            )}
          </div>
          {description && <p>{description}</p>}
        </div>
        {data.length > 0 ? (
          <ResponsiveContainer height={240}>
            <BarChart data={data} margin={{ left: 10, right: 20 }}>
              <CartesianGrid stroke={theme.grid} vertical={false} />
              <XAxis
                dataKey="dateLabel"
                interval="preserveStartEnd"
                tick={{
                  fill: theme.muted,
                  fontSize: chartFont(chartSizes.tickFontSize),
                }}
                axisLine={{ stroke: theme.grid }}
                tickLine={{ stroke: theme.grid }}
              />
              <YAxis
                tick={{
                  fill: theme.muted,
                  fontSize: chartFont(chartSizes.tickFontSize),
                }}
                axisLine={{ stroke: theme.grid }}
                tickLine={{ stroke: theme.grid }}
                label={buildYAxisLabel(valueTypeLabel)}
              />
              <Tooltip
                formatter={(value) => [formatValue(value), timeseriesValueLabel]}
                contentStyle={{
                  background: theme.tooltipBg,
                  border: `1px solid ${theme.border}`,
                  color: theme.text,
                }}
                itemStyle={{ color: theme.text }}
                labelStyle={{ color: theme.muted }}
              />
              <Bar
                isAnimationActive={false}
                dataKey="value"
                fill={theme.accentAlert}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p className="bd-empty">No data available.</p>
        )}
      </div>
    );
  }

  if (isBarChartType(entryType)) {
    const data = mapBarChartData(entry);
    const csvRows = [
      ["label", "value"],
      ...data.map((point) => [point.label, point.value ?? ""]),
    ];
    return (
      <div key={`${title}-${index}`} className="bd-chart-card">
        <div className="bd-chart-header">
          <div className="bd-chart-header-row">
            <h3>{title}</h3>
            {data.length > 0 && (
              <button
                className="bd-download-csv-button"
                type="button"
                onClick={() => downloadCsvFile(`${filenameBase}-bar.csv`, csvRows)}
              >
                Download CSV
              </button>
            )}
          </div>
          {description && <p>{description}</p>}
        </div>
        {data.length > 0 ? (
          <ResponsiveContainer height={360}>
            <BarChart data={data} margin={{ left: 10, right: 20 }}>
              <CartesianGrid stroke={theme.grid} vertical={false} />
              <XAxis
                dataKey="label"
                interval={0}
                tickFormatter={formatLongLabel}
                angle={chartSizes.angledLabelRotation}
                textAnchor="end"
                height={chartSizes.angledLabelHeight}
                tick={{
                  fill: theme.muted,
                  fontSize: chartFont(chartSizes.tickFontSize),
                }}
                axisLine={{ stroke: theme.grid }}
                tickLine={{ stroke: theme.grid }}
              />
              <YAxis
                tick={{
                  fill: theme.muted,
                  fontSize: chartFont(chartSizes.tickFontSize),
                }}
                axisLine={{ stroke: theme.grid }}
                tickLine={{ stroke: theme.grid }}
                label={buildYAxisLabel(valueTypeLabel)}
              />
              <Tooltip
                formatter={(value) => formatValue(value)}
                contentStyle={{
                  background: theme.tooltipBg,
                  border: `1px solid ${theme.border}`,
                  color: theme.text,
                }}
                itemStyle={{ color: theme.text }}
                labelStyle={{ color: theme.muted }}
              />
              <Bar
                isAnimationActive={false}
                dataKey="value"
                fill={theme.accentBrand}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p className="bd-empty">No data available.</p>
        )}
      </div>
    );
  }

  if (isGroupedBarType(entryType)) {
    const data = mapGroupedBarData(entry);
    const csvRows = [
      ["label", "internal", "external"],
      ...data.map((point) => [point.label, point.internal, point.external]),
    ];
    return (
      <div key={`${title}-${index}`} className="bd-chart-card">
        <div className="bd-chart-header">
          <div className="bd-chart-header-row">
            <h3>{title}</h3>
            {data.length > 0 && (
              <button
                className="bd-download-csv-button"
                type="button"
                onClick={() =>
                  downloadCsvFile(`${filenameBase}-grouped-bar.csv`, csvRows)
                }
              >
                Download CSV
              </button>
            )}
          </div>
          {description && <p>{description}</p>}
        </div>
        {data.length > 0 ? (
          <ResponsiveContainer height={360}>
            <BarChart data={data} margin={{ left: 10, right: 20 }}>
              <CartesianGrid stroke={theme.grid} vertical={false} />
              <XAxis
                dataKey="label"
                interval={0}
                tickFormatter={formatLongLabel}
                angle={chartSizes.angledLabelRotation}
                textAnchor="end"
                height={chartSizes.angledLabelHeight}
                tick={{
                  fill: theme.muted,
                  fontSize: chartFont(chartSizes.tickFontSize),
                }}
                axisLine={{ stroke: theme.grid }}
                tickLine={{ stroke: theme.grid }}
              />
              <YAxis
                allowDecimals={false}
                tick={{
                  fill: theme.muted,
                  fontSize: chartFont(chartSizes.tickFontSize),
                }}
                axisLine={{ stroke: theme.grid }}
                tickLine={{ stroke: theme.grid }}
                label={buildYAxisLabel(valueTypeLabel)}
              />
              <Tooltip
                formatter={(value, key) => [
                  formatValue(value),
                  key === "internal" ? "Internal" : "External",
                ]}
                contentStyle={{
                  background: theme.tooltipBg,
                  border: `1px solid ${theme.border}`,
                  color: theme.text,
                }}
                itemStyle={{ color: theme.text }}
                labelStyle={{ color: theme.muted }}
              />
              <Legend
                formatter={(value) =>
                  value === "internal" ? "Internal" : "External"
                }
                wrapperStyle={{ color: theme.muted }}
              />
              <Bar
                isAnimationActive={false}
                dataKey="internal"
                fill={theme.accentBrand}
                radius={[4, 4, 0, 0]}
              />
              <Bar
                isAnimationActive={false}
                dataKey="external"
                fill={theme.accentAlert}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p className="bd-empty">No data available.</p>
        )}
      </div>
    );
  }

  if (isTableType(entryType)) {
    const tableData = mapTableData(entry);
    const hasRows = tableData.rows.length > 0;
    const hasColumns = tableData.columns.length > 0;
    return (
      <div key={`${title}-${index}`} className="bd-chart-card">
        <div className="bd-chart-header">
          <h3>{title}</h3>
          {description && <p>{description}</p>}
        </div>
        {hasRows && hasColumns ? (
          <div className="bd-table-wrapper">
            <table className="bd-table">
              <thead>
                <tr>
                  <th>Month</th>
                  {tableData.columns.map((column) => (
                    <th key={column}>{column}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableData.rows.map((row, rowIndex) => (
                  <tr key={`${row.monthLabel}-${rowIndex}`}>
                    <td>{row.monthLabel}</td>
                    {tableData.columns.map((column) => (
                      <td key={column}>
                        {row.values[column] === 0 ? "" : formatValue(row.values[column])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="bd-empty">No data available.</p>
        )}
      </div>
    );
  }

  return (
    <div key={`${title}-${index}`} className="bd-chart-card">
      <div className="bd-chart-header">
        <h3>{title}</h3>
        {description && <p>{description}</p>}
      </div>
      <p className="bd-empty">Unsupported chart type.</p>
    </div>
  );
};

export default function WplDashboardPage() {
  const [rcaStats, setRcaStats] = useState([]);
  const [reportStats, setReportStats] = useState([]);
  const [wdStats, setWdStats] = useState([]);
  const [rcaLoading, setRcaLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(true);
  const [wdLoading, setWdLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("global");
  const [statsYear, setStatsYear] = useState(CURRENT_YEAR);
  const [availableYears, setAvailableYears] = useState([CURRENT_YEAR]);
  const [screensaver, setScreensaver] = useState(
    () => localStorage.getItem("wplScreensaver") === "1"
  );
  const [ssStepIndex, setSsStepIndex] = useState(0);
  const [ssDwell, setSsDwell] = useState(null);
  // Which step key is armed to receive a "data loaded" signal. Set from the
  // entry effect (not derived in render) so child readiness effects only fire
  // AFTER the step machine is armed — child effects run before parent ones.
  const [ssArmedKey, setSsArmedKey] = useState(null);
  const ssWaitingRef = useRef(false);
  const ssStepIndexRef = useRef(0);
  ssStepIndexRef.current = ssStepIndex;
  const ssFallbackTimerRef = useRef(null);
  // Bumped on every step entry. Timers capture it when armed and only advance
  // the cycle if their step is still current — a throttled timeout firing late
  // (background tab) must not skip a later step.
  const ssGenerationRef = useRef(0);
  const availableYearsRef = useRef(availableYears);
  availableYearsRef.current = availableYears;

  const clearScreensaverTimers = () => {
    clearTimeout(ssFallbackTimerRef.current);
  };

  const setScreensaverEnabled = (enabled) => {
    localStorage.setItem("wplScreensaver", enabled ? "1" : "0");
    if (enabled) setSsStepIndex(0);
    setScreensaver(enabled);
  };

  // Enter the current screensaver step: activate its tab, then wait for the
  // section to report its data loaded before starting the dwell countdown.
  useEffect(() => {
    if (!screensaver) {
      clearScreensaverTimers();
      ssWaitingRef.current = false;
      setSsArmedKey(null);
      return undefined;
    }
    const step = SCREENSAVER_STEPS[ssStepIndex];
    const generation = ++ssGenerationRef.current;
    setActiveTab(step.tab);
    if (step.key === "stats") {
      // The screensaver always shows the latest available year.
      setStatsYear((prev) => availableYearsRef.current[0] ?? prev);
    }
    ssWaitingRef.current = true;
    setSsDwell(null);
    setSsArmedKey(step.key);
    ssFallbackTimerRef.current = setTimeout(() => {
      if (ssGenerationRef.current !== generation) return;
      ssWaitingRef.current = false;
      setSsStepIndex((index) => (index + 1) % SCREENSAVER_STEPS.length);
    }, step.dwellMs + SCREENSAVER_FALLBACK_MS);
    return clearScreensaverTimers;
  }, [screensaver, ssStepIndex]);

  // Data-loaded signal from the active section: start the dwell countdown.
  // The step is resolved through a ref so a stale closure can never start a
  // dwell with the wrong duration.
  const handleScreensaverReady = (sourceKey) => {
    if (!screensaver || !ssWaitingRef.current) return;
    const stepIndex = ssStepIndexRef.current;
    const step = SCREENSAVER_STEPS[stepIndex];
    if (step.key !== sourceKey) return;
    ssWaitingRef.current = false;
    clearTimeout(ssFallbackTimerRef.current);
    setSsDwell({
      key: `${stepIndex}-${Date.now()}`,
      start: Date.now(),
      ms: step.dwellMs,
    });
  };

  // The dwell state drives the transition, so the countdown display and the
  // actual page switch always agree.
  useEffect(() => {
    if (!screensaver || !ssDwell) return undefined;
    const intervalId = setInterval(() => {
      if (Date.now() - ssDwell.start >= ssDwell.ms) {
        setSsDwell(null);
        setSsStepIndex((index) => (index + 1) % SCREENSAVER_STEPS.length);
      }
    }, 250);
    return () => clearInterval(intervalId);
  }, [screensaver, ssDwell]);

  const currentScreensaverStep = screensaver
    ? SCREENSAVER_STEPS[ssStepIndex]
    : null;

  useEffect(() => {
    let isMounted = true;
    bdDashboardAPI
      .bladeStatsYears()
      .then((data) => {
        if (!isMounted) return;
        if (Array.isArray(data?.years) && data.years.length > 0) {
          setAvailableYears(data.years);
        }
      })
      .catch(() => {
        // Year selector falls back to the current year only.
      });
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const refreshTimeouts = [];
    const refreshIntervals = [];
    setRcaLoading(true);
    setReportLoading(true);
    setWdLoading(true);
    document.body.classList.add("bd-dashboard-body");
    const updateRcaStats = ({ setLoading = false } = {}) => {
      if (setLoading && isMounted) {
        setRcaLoading(true);
      }
      bdDashboardAPI
        .rcaStatistics({ year: statsYear })
        .then((rcaData) => {
          if (!isMounted) return;
          setRcaStats(Array.isArray(rcaData) ? rcaData : []);
          setError(null);
        })
        .catch((err) => {
          if (!isMounted) return;
          setError(err);
          setRcaStats([]);
        })
        .finally(() => {
          if (!isMounted || !setLoading) return;
          setRcaLoading(false);
        });
    };

    const updateReportStats = ({ setLoading = false } = {}) => {
      if (setLoading && isMounted) {
        setReportLoading(true);
      }
      bdDashboardAPI
        .reportStatistics({ year: statsYear })
        .then((reportData) => {
          if (!isMounted) return;
          setReportStats(Array.isArray(reportData) ? reportData : []);
          setError(null);
        })
        .catch((err) => {
          if (!isMounted) return;
          setError(err);
          setReportStats([]);
        })
        .finally(() => {
          if (!isMounted || !setLoading) return;
          setReportLoading(false);
        });
    };

    const updateWdStats = ({ setLoading = false } = {}) => {
      if (setLoading && isMounted) {
        setWdLoading(true);
      }
      bdDashboardAPI
        .windDiagnosticsStatistics({ year: statsYear })
        .then((wdData) => {
          if (!isMounted) return;
          setWdStats(Array.isArray(wdData) ? wdData : []);
          setError(null);
        })
        .catch((err) => {
          if (!isMounted) return;
          setError(err);
          setWdStats([]);
        })
        .finally(() => {
          if (!isMounted || !setLoading) return;
          setWdLoading(false);
        });
    };

    updateRcaStats({ setLoading: true });
    updateReportStats({ setLoading: true });
    updateWdStats({ setLoading: true });

    const intervalMs = 10 * 60 * 1000;
    const staggerMs = intervalMs / 3;
    const refreshJobs = [
      updateRcaStats,
      updateReportStats,
      updateWdStats,
    ];

    refreshJobs.forEach((job, index) => {
      const timeoutId = setTimeout(() => {
        job();
        const intervalId = setInterval(job, intervalMs);
        refreshIntervals.push(intervalId);
      }, intervalMs + staggerMs * index);
      refreshTimeouts.push(timeoutId);
    });

    return () => {
      isMounted = false;
      refreshTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
      refreshIntervals.forEach((intervalId) => clearInterval(intervalId));
      document.body.classList.remove("bd-dashboard-body");
    };
  }, [statsYear]);

  const reportValueStats = useMemo(
    () => mapValueStats(reportStats),
    [reportStats]
  );
  const reportChartEntries = useMemo(
    () => reportStats.filter((entry) => getEntryType(entry) !== "value"),
    [reportStats]
  );

  const wdValueStats = useMemo(() => mapValueStats(wdStats), [wdStats]);
  const wdChartEntries = useMemo(
    () => wdStats.filter((entry) => getEntryType(entry) !== "value"),
    [wdStats]
  );

  const rcaValueStats = useMemo(() => mapValueStats(rcaStats), [rcaStats]);
  const rcaChartEntries = useMemo(
    () => rcaStats.filter((entry) => getEntryType(entry) !== "value"),
    [rcaStats]
  );

  const statsLoading = rcaLoading || reportLoading || wdLoading;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!statsLoading) handleScreensaverReady("stats");
  }, [statsLoading, screensaver, ssStepIndex]);
  // Cards: Clients served, Windfarms served, No. of blades assessed, RCA
  // Performed. Charts: Defects over time, Defect types, Blade types.
  const bladeValueStats = [
    ...reportValueStats,
    ...wdValueStats,
    ...rcaValueStats,
  ];
  const bladeChartEntries = [
    ...wdChartEntries,
    ...reportChartEntries,
    ...rcaChartEntries,
  ];
  const tabs = [
    { key: "global", label: "Global View" },
    { key: "stats", label: `${statsYear} Blade Statistics` },
    { key: "windfarms", label: "Lassie" },
  ];

  return (
    <div className="bd-dashboard">
      <div className="bd-page-header">
        <h1>WPL Dashboard</h1>
        <button
          type="button"
          className={`bd-screensaver-toggle${
            screensaver ? " bd-screensaver-on" : ""
          }`}
          onClick={() => setScreensaverEnabled(!screensaver)}
          title="Auto-cycle the tabs for the office screen"
        >
          {screensaver ? (
            <>
              <ScreensaverCountdown dwell={ssDwell} />
              Screensaver on
            </>
          ) : (
            "▶ Screensaver"
          )}
        </button>
      </div>

      <div className="bd-tab-bar" role="tablist">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.key}
            className={`bd-tab${activeTab === tab.key ? " bd-tab-active" : ""}`}
            onClick={() => {
              if (screensaver) setScreensaverEnabled(false);
              setActiveTab(tab.key);
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "global" && (
        <WplGlobalViewSection
          onLoaded={() => handleScreensaverReady("global")}
          screensaverWaiting={screensaver && ssArmedKey === "global"}
        />
      )}

      {activeTab === "windfarms" && (
        <WplActiveWindfarmsSection
          screensaverPhase={
            currentScreensaverStep?.tab === "windfarms"
              ? currentScreensaverStep.phase
              : null
          }
          onScreensaverReady={(phase) =>
            handleScreensaverReady(
              phase === "farm" ? "lassie-farm" : "lassie-overview"
            )
          }
        />
      )}

      {activeTab !== "windfarms" && error && (
        <p className="bd-error">
          Could not load dashboard statistics. Please try again.
        </p>
      )}
      {activeTab === "stats" && (
        <section className="bd-section">
          <div className="bd-leads-header">
            <h2>{`${statsYear} Blade Statistics`}</h2>
            <select
              className="bd-access-mode"
              value={statsYear}
              onChange={(e) => setStatsYear(Number(e.target.value))}
            >
              {availableYears.map((yearOption) => (
                <option key={yearOption} value={yearOption}>
                  {yearOption}
                </option>
              ))}
            </select>
          </div>
          {statsLoading ? (
            <div className="bd-section-skeleton">
              <div className="bd-skeleton-card" />
              <div className="bd-skeleton-chart" />
            </div>
          ) : (
            <>
              {!error &&
                bladeValueStats.length === 0 &&
                bladeChartEntries.length === 0 && (
                  <p className="bd-empty">No statistics available yet.</p>
                )}
              {bladeValueStats.length > 0 && (
                <DashboardCard statistics={bladeValueStats} />
              )}
              <div className="bd-report-grid">
                {bladeChartEntries.map((entry, index) =>
                  renderStatEntry(entry, index, { timeseriesValueLabel: "Value" })
                )}
              </div>
            </>
          )}
        </section>
      )}
    </div>
  );
}
